import React, { useState, useEffect, useCallback } from 'react';
import { notesApi } from '../api';
import ConfirmDialog from '../components/common/ConfirmDialog';
import NoteDetailModal from '../components/notes/NoteDetailModal';
import NoteModal from '../components/notes/NoteModal';
import { Icon } from '../components/common/Icons';
import { toast } from 'react-toastify';

function formatDormancy(days) {
  if (days == null || days <= 0) return 'Opened recently';
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  if (months === 1) return '1 month ago';
  if (months < 12) return `${months} months ago`;
  const years = Math.floor(days / 365);
  return `${years} ${years === 1 ? 'year' : 'years'} ago`;
}

export default function CleanupPage() {
  const [notes, setNotes] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [deleteNote, setDeleteNote] = useState(null);
  const [viewNote, setViewNote] = useState(null);
  const [editNote, setEditNote] = useState(null);

  const loadDormantNotes = useCallback(async (targetPage = 0, forceShowAll = showAll) => {
    setLoading(true);
    try {
      const { data } = await notesApi.getCleanup({ page: targetPage, size: 10, all: forceShowAll });
      setNotes(data?.content || []);
      setTotalPages(data?.totalPages || 0);
      setPage(data?.page || 0);
    } catch {
      toast.error('Failed to load dormant notes');
    } finally {
      setLoading(false);
    }
  }, [showAll]);

  useEffect(() => {
    loadDormantNotes(0, false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Soft delete / Move to Trash
  const handleMoveToTrash = async () => {
    if (!deleteNote) return;
    try {
      await notesApi.delete(deleteNote.id);
      toast.success(`"${deleteNote.title}" moved to Trash`);
      setDeleteNote(null);
      loadDormantNotes(page, showAll);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to move note to trash');
    }
  };

  const handleToggleFilter = (all) => {
    setShowAll(all);
    loadDormantNotes(0, all);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < totalPages) {
      loadDormantNotes(newPage, showAll);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className="cleanup-page">
      <div className="page-header">
        <div className="page-header-content">
          <div className="page-title-row">
            <h1 className="page-title">Workspace Cleanup</h1>
            <span className="badge-tag info">Maintenance</span>
          </div>
          <p className="page-subtitle">
            Review your least-accessed notes and free up clutter. Notes moved to trash can be restored within 7 days.
          </p>
        </div>
      </div>

      {/* Control Strip */}
      <div className="cleanup-control-strip">
        <div className="cleanup-toggle-group">
          <button
            type="button"
            className={`btn-toggle ${!showAll ? 'active' : ''}`}
            onClick={() => handleToggleFilter(false)}
          >
            Inactive Notes (30+ days)
          </button>
          <button
            type="button"
            className={`btn-toggle ${showAll ? 'active' : ''}`}
            onClick={() => handleToggleFilter(true)}
          >
            All Notes by Activity
          </button>
        </div>

        <div className="cleanup-meta-info">
          {!loading && notes.length > 0 && (
            <span>Showing least recently accessed notes</span>
          )}
        </div>
      </div>

      {/* Table Content */}
      {loading ? (
        <div className="loading-state-wrap">
          <div className="spinner" />
          <p className="loading-text">Analyzing workspace activity...</p>
        </div>
      ) : notes.length === 0 ? (
        <div className="empty-state-card">
          <div className="empty-state-icon-wrap success">
            <Icon name="check" size={28} />
          </div>
          <h2 className="empty-state-title">Your workspace is clean!</h2>
          <p className="empty-state-desc">
            {!showAll
              ? "None of your notes have been inactive for more than 30 days. You're actively engaging with your content!"
              : "No notes found in your workspace."}
          </p>
          {!showAll && (
            <button
              type="button"
              className="btn-ghost"
              onClick={() => handleToggleFilter(true)}
            >
              View all notes ranked by activity
            </button>
          )}
        </div>
      ) : (
        <div className="cleanup-table-wrapper">
          <div className="cleanup-table-card">
            <div className="cleanup-table-header">
              <div className="th-note th-doc">Note</div>
              <div className="th-folder">Category</div>
              <div className="th-staleness">Last Opened</div>
              <div className="th-views">Views</div>
              <div className="th-actions">Delete</div>
            </div>

            <div className="cleanup-table-body">
              {notes.map(note => (
                <div key={note.id} className="cleanup-row">
                  <div
                    className="cell-note cell-doc"
                    onClick={() => setViewNote(note)}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="cell-note-icon cell-doc-icon">
                      <Icon name="note" size={16} />
                    </div>
                    <div className="cell-note-info cell-doc-info">
                      <div className="cell-note-title cell-doc-title">{note.title}</div>
                      <div className="cell-note-preview cell-doc-preview">{note.contentSnippet || note.content}</div>
                      {note.tags?.length > 0 && (
                        <div className="cell-note-tags cell-doc-tags">
                          {Array.from(note.tags).slice(0, 2).map(t => (
                            <span key={t} className="tag-badge sm">#{t}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="cell-folder">
                    <span className="folder-pill sm">
                      <Icon name="category" size={12} />
                      {note.categoryName || note.folderName || 'General'}
                    </span>
                  </div>

                  <div className="cell-staleness">
                    <span className="staleness-text">{formatDormancy(note.daysSinceLastAccess)}</span>
                  </div>

                  <div className="cell-views">
                    <span className="views-count">
                      <Icon name="eye" size={12} />
                      {note.hitCount ?? 0}
                    </span>
                  </div>

                  <div className="cell-actions">
                    <button
                      type="button"
                      className="btn-icon danger sm"
                      onClick={() => setDeleteNote(note)}
                      title="Move note to trash"
                      aria-label="Move note to trash"
                    >
                      <Icon name="trash" size={15} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Simple Pagination */}
          {totalPages > 1 && (
            <div className="pagination-bar" style={{ marginTop: '1.25rem', display: 'flex', justifyContent: 'center', gap: '0.5rem' }}>
              <button
                type="button"
                className="btn-ghost sm"
                disabled={page === 0}
                onClick={() => handlePageChange(page - 1)}
              >
                Previous
              </button>
              <span style={{ display: 'flex', alignItems: 'center', padding: '0 0.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                Page {page + 1} of {totalPages}
              </span>
              <button
                type="button"
                className="btn-ghost sm"
                disabled={page >= totalPages - 1}
                onClick={() => handlePageChange(page + 1)}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      {viewNote && (
        <NoteDetailModal
          note={viewNote}
          onClose={() => setViewNote(null)}
          onEdit={d => { setViewNote(null); setEditNote(d); }}
          onNoteUpdated={updated => {
            setNotes(prev => prev.map(n => n.id === updated.id ? { ...n, ...updated } : n));
          }}
        />
      )}

      {editNote && (
        <NoteModal
          note={editNote}
          onClose={() => setEditNote(null)}
          onSaved={() => loadDormantNotes(page)}
        />
      )}

      {deleteNote && (
        <ConfirmDialog
          title="Move Note to Trash?"
          message={`"${deleteNote.title}" will be moved to Trash. You can recover it within 7 days.`}
          confirmLabel="Move to Trash"
          onConfirm={handleMoveToTrash}
          onCancel={() => setDeleteNote(null)}
          danger
        />
      )}
    </div>
  );
}
